"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShareLinkPopup = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const typography_1 = require("../typography");
const buttons_1 = require("../buttons");
const card_1 = require("../card/card");
const styled_1 = __importDefault(require("@emotion/styled"));
const react_dom_1 = require("react-dom");
const react_copy_to_clipboard_1 = __importDefault(require("react-copy-to-clipboard"));
const ShareLinkPopup = ({ open, onClose, artifactId, position, containerRef, link, refreshLink, companyName, anonymousShareEnabled, cardSize, }) => {
    var _a;
    const popupRef = (0, react_1.useRef)(null);
    const activeElement = (0, react_1.useRef)(document.activeElement);
    const closeAndReturnFocus = (0, react_1.useCallback)(() => {
        var _a, _b;
        (_b = (_a = activeElement.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
        onClose();
    }, [activeElement, onClose]);
    const handleKeydown = (e) => {
        if (e.key === 'Escape') {
            e.nativeEvent.stopImmediatePropagation();
            closeAndReturnFocus();
        }
    };
    const handleDocumentClick = (0, react_1.useCallback)((e) => {
        var _a;
        if (!((_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
            closeAndReturnFocus();
        }
    }, [popupRef, closeAndReturnFocus]);
    (0, react_1.useEffect)(() => {
        var _a, _b;
        if (open) {
            refreshLink();
            document.addEventListener('click', handleDocumentClick, true);
            (_b = (_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('input')) === null || _b === void 0 ? void 0 : _b.focus();
        }
        return () => document.removeEventListener('click', handleDocumentClick, true);
    }, [open, handleDocumentClick, refreshLink]);
    const autoSelectInputText = (e) => {
        e.target.select();
    };
    const ignoreInputEvents = e => {
        e.preventDefault();
    };
    const idDescriptorText = artifactId ? 'artifact' : 'call';
    const warningText = anonymousShareEnabled
        ? `Caution. Anyone with this link can view this call and media.`
        : `Anyone on the ${companyName || 'company'} team can view this call and media.`;
    const showError = /error/gi.test(link || '');
    const showLoading = link === null;
    return (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsx)(Popup, Object.assign({ hidden: !open, onKeyDown: handleKeydown, ref: popupRef, top: position === null || position === void 0 ? void 0 : position.top, right: position === null || position === void 0 ? void 0 : position.right }, { children: (0, jsx_runtime_1.jsxs)(PopupCard, Object.assign({ shadow: "xlarge", decoratorPosition: "side", "data-testid": "share-link-popup", size: cardSize }, { children: [showError && ((0, jsx_runtime_1.jsx)(typography_1.AppText, Object.assign({ as: "p", "data-testid": "share-link-popup-error" }, { children: "There was a problem creating the link. Reopen this dialog to try again." }))), !showError && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)(ControlsContainer, { children: [(0, jsx_runtime_1.jsx)(typography_1.Label, Object.assign({ screenReaderOnly: true, htmlFor: `shareable-${idDescriptorText}-details-link` }, { children: "Link to share" })), (0, jsx_runtime_1.jsx)(ReadOnlyInput, { type: "text", id: `shareable-${idDescriptorText}-details-link`, "data-testid": "share-link-input", value: showLoading ? 'Generating link...' : link, onFocus: autoSelectInputText, onChange: ignoreInputEvents }), (0, jsx_runtime_1.jsx)(CopyToClipboardPrimaryButton, { text: link || '', "data-testid": "share-link-button" })] }), (0, jsx_runtime_1.jsx)(WarningText, Object.assign({ "data-testid": "share-link-help-text" }, { children: warningText }))] }))] })) })), (_a = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) !== null && _a !== void 0 ? _a : document.body);
};
exports.ShareLinkPopup = ShareLinkPopup;
const CopyToClipboardPrimaryButton = ({ text, onCopy, 'data-testid': testId, }) => {
    const [buttonText, setButtonText] = (0, react_1.useState)('Copy');
    const handleCopyClick = (0, react_1.useCallback)(() => {
        setButtonText('Copied');
        onCopy === null || onCopy === void 0 ? void 0 : onCopy();
        setTimeout(() => setButtonText('Copy'), 1500);
    }, [setButtonText, onCopy]);
    return ((0, jsx_runtime_1.jsx)(react_copy_to_clipboard_1.default, Object.assign({ text: text, onCopy: handleCopyClick }, { children: (0, jsx_runtime_1.jsx)(buttons_1.Button, Object.assign({ "data-testid": testId, style: { width: '105px' }, name: "copy" }, { children: buttonText })) })));
};
const WarningText = styled_1.default.p `
    color: ${({ theme }) => theme.text.color.medium};
    font-size: ${({ theme }) => theme.text.size.small};
`;
const ControlsContainer = styled_1.default.div `
    display: flex;
`;
const ReadOnlyInput = styled_1.default.input `
    width: 100%;
    font-size: 1.1rem;
    margin-right: 1rem;
    line-height: inherit;
    border: 1px solid ${({ theme }) => theme.colors.grey90};
    padding: 0.5rem;
`;
const PopupCard = (0, styled_1.default)(card_1.Card) `
    width: ${props => props.size}px;
    display: grid;
    grid-gap: 0.25rem;
    line-height: 1.4;
    padding: 1.5rem 1.5rem 1rem 2rem;
    z-index: ${({ theme }) => theme.layers.ui};
`;
const Popup = styled_1.default.div `
    position: absolute;
    top: ${props => props.top}px;
    right: ${props => props.right}px;
    overflow: hidden;
    padding: 24px 24px 40px 24px;
`;
//# sourceMappingURL=share_link_popup.js.map